import { useLocalStorage } from "@/auth/localStorage.tsx";
import type { paths as backendPaths } from "@/client/backend/zz_schema";
import createClient from "openapi-fetch";
import {
	type FC,
	type ReactNode,
	createContext,
	useContext,
	useEffect,
	useState,
} from "react";
import { Navigate, Outlet } from "react-router-dom";
import { Limrun } from "@limrun/api";

export type AuthContextProps =
	| {
			userID: string;
			userName: string;
			client: ReturnType<typeof createClient<backendPaths>>;
			regionClient: Limrun;
			apiEndpoint: string;
			token: string;
	  }
	| "loading"
	| "unauthenticated";

export const AuthContext = createContext<AuthContextProps>("loading");

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	const [tokenInStorage] = useLocalStorage("token");
	const [organizationTid] = useLocalStorage("organizationTid");
	const [authState, setAuthState] = useState<AuthContextProps>("loading");

	useEffect(() => {
		const verifyToken = async () => {
			if (!tokenInStorage) {
				setAuthState("unauthenticated");
				return;
			}
			const backend = createClient<backendPaths>({
				baseUrl: import.meta.env.VITE_API_ENDPOINT as string,
				headers: {
					Authorization: `Bearer ${tokenInStorage}`,
				},
			})
			const user = await backend.GET("/whoami");
			if (user.response.status !== 200 || !user.data) {
				setAuthState("unauthenticated");
				return;
			}
			if (user.data.type !== "user" || !user.data.user) {
				setAuthState("unauthenticated");
				return;
			}
			let regionUrl = import.meta.env.VITE_LIMRUN_API_ENDPOINT as string;
			if (organizationTid === "org_01k3v8mbmkfysaqkvd1dt1b2tj" || // ncr voyix prod
				organizationTid === "org_01k3v8mbmvfys91nqpym6zx880" // ncr voyix mirror prod
			) {
				regionUrl = "https://eu-north1.limbar.net";
			}
			if (organizationTid === "org_01k3v8bvxfecfr8vjqa1jd9rrk"){
				regionUrl = "https://eu-staging.limbar.dev";
			} // ncr voyix mirror staging 
			setAuthState({
				apiEndpoint: import.meta.env.VITE_API_ENDPOINT as string,
				userID: user.data.user.id,
				userName: user.data.user.name,
				client: backend,
				regionClient: new Limrun({
					apiKey: tokenInStorage,
					baseURL: regionUrl,
					...(organizationTid ? { defaultHeaders: {
						"X-Limrun-Organization": organizationTid,
					} } : {}),
				 }),
				token: tokenInStorage,
			});
		};
		verifyToken().catch((error) => {
			console.log(`Token verification failed: ${error}`);
			setAuthState("unauthenticated");
		});
	}, [tokenInStorage, organizationTid]);

	useEffect(() => {
		const interval = setInterval(() => {
			if (authState !== "unauthenticated" && authState !== "loading" && typeof window !== "undefined") {
				window.zaraz.set("user_id", authState.userID);
				clearInterval(interval);
			}
		}, 100);

		return () => clearInterval(interval);
	}, [authState]);

	return (
		<AuthContext.Provider value={authState}>{children}</AuthContext.Provider>
	);
};

export const ProtectedRoute: FC = () => {
	const context = useContext(AuthContext);
	switch (context) {
		case "unauthenticated":
			return <Navigate to="/authn/login" />;
		case "loading":
			return <div>Loading...</div>;
		default:
			return <Outlet />;
	}
};
